import React, { useContext } from 'react';
import styled from 'styled-components';
import { BodyMixin, SitemapMixin } from '@qover/typography';
import { TicketIcon } from '@qover/icons/TicketIcon';
import { Popover } from '@qover/molecules';
import { Trans } from '@lingui/react';
import { SummaryDiscountForm } from '../forms';
import { Price } from './summary.styles';
import { FormattedCurrency } from '@qover/react-intl';
import { DiscountCode } from '@qover/domain';
import { useFeature } from '@qover/hooks';

const DiscountContainer = styled.div`
  border-top: 1px solid var(--color-text-quote-10);
  display: flex;
  flex-direction: column;
  padding: 9px 0 10px;
  width: 100%;
`;

const DiscountInnerContainer = styled.div`
  ${SitemapMixin};
  align-items: center;
  background-color: ${props => props.theme.colors.textQuote(0.05)};
  color: ${props => props.theme.colors.secondary()};
  display: flex;
  padding: 7px 10px;

  > button {
    color: currentColor;
    background: none;
    padding: 0;
    margin-left: 6px;
  }

  > span {
    display: flex;
    align-items: center;

    svg {
      margin-right: 5px;
    }
  }

  svg {
    path {
      fill: currentColor;
    }
  }
`;

const DiscountPopover = styled.span`
  ${BodyMixin};
  color: ${({ theme }) => theme.colors.secondary()};
  cursor: pointer;
  margin: 0;
  text-decoration: underline;

  :hover {
    text-decoration: none;
  }
`;

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="10"
    viewBox="0 0 10 10"
  >
    <defs>
      <path
        id="discount-a"
        d="M9.708.292c.365.366.388.945.068 1.337l-.068.076L6.412 5l3.296 3.295c.367.367.388.949.064 1.341l-.064.072c-.366.365-.945.388-1.337.068l-.076-.068L5 6.412 1.705 9.708c-.367.367-.949.388-1.341.064l-.072-.064c-.365-.366-.388-.945-.068-1.337l.068-.076L3.588 5 .292 1.705C-.075 1.338-.096.756.228.364L.292.292c.366-.365.945-.388 1.337-.068l.076.068L5 3.588 8.295.292c.367-.367.949-.388 1.341-.064l.072.064z"
      />
    </defs>
    <g fillRule="evenodd">
      <mask id="discount-b">
        <use xlinkHref="#discount-a" />
      </mask>
      <use fillRule="nonzero" xlinkHref="#discount-a" />
      <g fillRule="nonzero" mask="url(#discount-b)">
        <path d="M0 0H10V10H0z" />
      </g>
    </g>
  </svg>
);

type SummaryDiscountProps = {
  addDiscount: (discountCode: string) => Promise<void>;
  discountAmount?: number;
  discountCode?: DiscountCode;
  discountInformations: React.ReactNode;
  paymentRythmMessage?: React.ReactNode;
  removeDiscount: (discountCode: string) => Promise<void>;
};

export function SummaryDiscount(props: SummaryDiscountProps) {
  const showDiscount = useFeature('showDiscount');

  if (!showDiscount) {
    return null;
  }

  return (
    <DiscountContainer>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <SummaryDiscountForm
          addDiscount={props.addDiscount}
          discountCode={props.discountCode}
          removeDiscount={props.removeDiscount}
          small
        >
          <DiscountInnerContainer>
            <span className="font-bold">
              <TicketIcon />
              {props.discountCode}
            </span>
            {props.removeDiscount && (
              <button
                onClick={() => {
                  // @ts-ignore
                  props.removeDiscount(props.discountCode);
                }}
                type="button"
              >
                <CloseIcon />
              </button>
            )}
          </DiscountInnerContainer>
        </SummaryDiscountForm>
        {!!props.discountAmount && props.discountCode && (
          <Price data-test="general.summaryCard.discountAmount">
            <FormattedCurrency value={props.discountAmount} />
            {props.paymentRythmMessage}
          </Price>
        )}
      </div>
      {props.discountInformations && (
        <Popover
          button={
            <DiscountPopover>
              <Trans id="general.cart.discountPopover" />
            </DiscountPopover>
          }
        >
          {props.discountInformations}
        </Popover>
      )}
    </DiscountContainer>
  );
}
